import { useContext, useMemo, useEffect, useCallback } from "react"
import PropTypes from "prop-types"
import { isEqual } from "lodash"
import MetricsSelector from "components/custom-metrics-selector"
import { useMetricsSelectorAnalytics } from "components/custom-metrics-selector/hooks/use-metrics-selector-analytics"
import { getCurrentTemplateMetrics, EMPTY_ARRAY } from "./helpers"

export default function MetricsSelectorWrapper({
  context,
  templateMetadata,
  selectedMetrics,
  availableMetrics,
  setSelectedMetrics,
}) {
  const assetViewContext = useContext(context)
  const {
    setView,
    view,
    deleteTemplate,
    saveTemplate,
    setShowMetricsSelector,
    showMetricsSelector,
    modelId,
  } = assetViewContext
  const analytics = useMetricsSelectorAnalytics(assetViewContext)

  const currentMetrics = useMemo(() => {
    if (!templateMetadata || !templateMetadata.config.metrics) {
      return []
    }
    return getCurrentTemplateMetrics(templateMetadata.config.metrics, modelId)
  }, [templateMetadata, modelId])

  useEffect(() => {
    // Avoid setting selectedMetrics if they are equivalent to the current metrics (prevents infinite re-renders)
    if (!isEqual(selectedMetrics, currentMetrics)) {
      setSelectedMetrics(currentMetrics)
    }
  }, [currentMetrics, selectedMetrics, setSelectedMetrics])

  const handleToggle = useCallback(() => {
    if (showMetricsSelector) {
      analytics.trackClosed(templateMetadata)
    } else {
      analytics.trackOpened(templateMetadata)
    }
    setShowMetricsSelector(!showMetricsSelector)
  }, [analytics, showMetricsSelector, setShowMetricsSelector, templateMetadata])

  const handleSaveMetricsTemplate = useCallback(async (template) => {
    const metricsCount = Object.values(template.metrics || {}).filter(Boolean).length

    if (templateMetadata?.id) {
      analytics.trackViewUpdated(templateMetadata.id, template.name, metricsCount)
    } else {
      analytics.trackViewSaved(template.name, metricsCount)
    }

    const savedTemplate = await saveTemplate({ id: templateMetadata?.id, ...template })
    if (savedTemplate && view !== savedTemplate.id) {
      setView(savedTemplate.id)
    }
  }, [analytics, saveTemplate, setView, templateMetadata, view])

  const handleDeleteCustomTemplate = useCallback(async (template) => {
    analytics.trackViewDeleted(template.id, template.name)
    await deleteTemplate(template)
    setView("exposure")
  }, [analytics, deleteTemplate, setView])

  return <MetricsSelector
    // This is needed for re-calculating the state within the metrics selector
    key={showMetricsSelector ? `${templateMetadata?.id || "new"}-${showMetricsSelector}-${modelId}` : "closed"}
    modelId={modelId}
    isOpen={showMetricsSelector}
    toggle={handleToggle}
    setSelectedMetrics={(metrics) => setSelectedMetrics(metrics)}
    includeMetricTypes
    defaultSelectedMetrics={EMPTY_ARRAY}
    availableMetrics={availableMetrics}
    saveCustomTemplate={handleSaveMetricsTemplate}
    deleteCustomTemplate={handleDeleteCustomTemplate}
    customTemplate={templateMetadata}
    analytics={analytics}
  />
}

MetricsSelectorWrapper.propTypes = {
  context: PropTypes.object.isRequired,
  templateMetadata: PropTypes.object.isRequired,
  availableMetrics: PropTypes.array.isRequired,
  setSelectedMetrics: PropTypes.func.isRequired,
  selectedMetrics: PropTypes.array.isRequired,
}
